import { Component, Inject, OnDestroy, Optional } from '@angular/core';
import {
    DwAction,
    DwActionButtonComponent,
    DwActionPreference,
    DwActionType,
    DwDecoratorType,
    DwEventService,
    DwMetaDataFormApi,
    DwMetaDataFormStateService,
    DwOrmDataService,
    DwOrmDataServiceToken,
    DwUiConfigContextBase
} from '@devwareapps/devware-cap';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { AppMetaData, AppMetaDataItemNames, StudentCourseEntity } from '../../../meta-data/app-meta-data.service';

@DwAction({
    keyName: 'student-course-reporting-visibility',
    display: 'Student Course - Reporting Visibility',
    actionType: DwActionType.formAction,
    parentItemName: AppMetaDataItemNames.StudentCourse,
    decoratorType: DwDecoratorType.component,
    isGlobal: false,
    isButtonComponent: true,
    actionMessage: 'Saving...',
    defaultButtonConfig: {
        useParentDisplay: false,
        buttonText: 'Visible in reporting',
        iconClass: 'fa fa-eye',
        buttonClass: 'btn btn-secondary',
        buttonTitle: 'Toggle whether this student course appears in reporting'
    },
    defaultSecurityConfig: {
        requireAuthentication: true,
        requireCanUpdate: true
    }
})
@Component({
    selector: 'app-student-course-reporting-visibility-action',
    template: `
        <button type="button"
                class="student-course-reporting-toggle"
                [class.student-course-reporting-toggle--saving]="saving"
                [class.student-course-reporting-toggle--checked]="visibleInReporting"
                [disabled]="disabled"
                [attr.aria-pressed]="visibleInReporting"
                [title]="buttonTitle"
                (click)="toggleReportingVisibility()">
            <span class="student-course-reporting-toggle__switch" aria-hidden="true">
                <span class="student-course-reporting-toggle__knob"></span>
            </span>
            <span class="student-course-reporting-toggle__label">Visible in reporting</span>
        </button>
    `,
    styles: [`
        :host {
            display: inline-flex;
        }

        .student-course-reporting-toggle {
            align-items: center;
            background: var(--surface-base);
            border: 1px solid var(--border-default);
            border-radius: var(--radius-lg);
            color: var(--text-secondary);
            display: inline-flex;
            font-size: 13px;
            font-weight: 600;
            gap: 8px;
            min-height: 36px;
            padding: 7px 12px;
            transition: border-color .15s ease, box-shadow .15s ease, color .15s ease;
        }

        .student-course-reporting-toggle:hover:not(:disabled),
        .student-course-reporting-toggle:focus-visible {
            border-color: color-mix(in srgb, var(--dw-navbar-bg) 34%, var(--border-strong));
            box-shadow: 0 0 0 3px color-mix(in srgb, var(--dw-navbar-bg) 12%, transparent);
            color: var(--text-primary);
            outline: none;
        }

        .student-course-reporting-toggle:disabled {
            cursor: not-allowed;
            opacity: .65;
        }

        .student-course-reporting-toggle__switch {
            background: var(--border-strong);
            border-radius: 999px;
            display: inline-flex;
            height: 16px;
            padding: 2px;
            transition: background-color .15s ease;
            width: 30px;
        }

        .student-course-reporting-toggle__knob {
            background: var(--surface-base);
            border-radius: 50%;
            box-shadow: 0 1px 2px rgba(15, 23, 42, .24);
            display: block;
            height: 12px;
            transform: translateX(0);
            transition: transform .15s ease;
            width: 12px;
        }

        .student-course-reporting-toggle--checked .student-course-reporting-toggle__switch {
            background: var(--dw-navbar-bg);
        }

        .student-course-reporting-toggle--checked .student-course-reporting-toggle__knob {
            transform: translateX(14px);
        }

        .student-course-reporting-toggle--saving .student-course-reporting-toggle__knob {
            opacity: .75;
        }

        @media (max-width: 480px) {
            :host {
                margin-top: 0;
                max-width: 50vw;
                padding-top: 0;
            }

            .student-course-reporting-toggle {
                font-size: 12px;
                gap: 6px;
                min-height: 32px;
                max-width: 50vw;
                padding: 4px 8px;
                white-space: nowrap;
            }

            .student-course-reporting-toggle__switch {
                height: 14px;
                padding: 2px;
                width: 26px;
            }

            .student-course-reporting-toggle__knob {
                height: 10px;
                width: 10px;
            }

            .student-course-reporting-toggle--checked .student-course-reporting-toggle__knob {
                transform: translateX(12px);
            }
        }
    `]
})
export class StudentCourseReportingVisibilityActionComponent implements DwActionButtonComponent, OnDestroy {
    actionPreference: DwActionPreference<any, any>;
    actionContext?: DwUiConfigContextBase;

    visibleInReporting = false;
    saving = false;

    private formApi: DwMetaDataFormApi;
    private formDataSubscription: Subscription;
    private studentCourse: StudentCourseEntity;

    constructor(
        @Optional() private dwFormStateService: DwMetaDataFormStateService,
        @Inject(DwOrmDataServiceToken) private dataService: DwOrmDataService,
        private dwEventService: DwEventService
    ) {
        this.formApi = this.dwFormStateService?.state?.formApi;
    }

    get buttonTitle(): string {
        return this.actionPreference?.buttonConfig?.buttonTitle || 'Toggle whether this student course appears in reporting';
    }

    get disabled(): boolean {
        return this.saving
            || this.actionPreference?.buttonConfig?.disabled
            || !this.studentCourse?.StudentCourseId;
    }

    setupComponent(): void {
        this.setupStudentCourse(this.formApi?.getFormData<StudentCourseEntity>());

        if (this.formDataSubscription) {
            this.formDataSubscription.unsubscribe();
        }

        this.formDataSubscription = this.formApi?.getFormDataChanges(100)
            .subscribe(() => this.setupStudentCourse(this.formApi.getFormData<StudentCourseEntity>()));
    }

    ngOnDestroy(): void {
        if (this.formDataSubscription) {
            this.formDataSubscription.unsubscribe();
        }
    }

    toggleReportingVisibility(): void {
        if (this.disabled) {
            return;
        }

        this.saveReportingVisibility(!this.visibleInReporting);
    }

    private setupStudentCourse(studentCourse: StudentCourseEntity): void {
        this.studentCourse = studentCourse;
        this.visibleInReporting = !studentCourse?.HideInReporting;
    }

    private saveReportingVisibility(visibleInReporting: boolean): void {
        const studentCourse = AppMetaData.StudentCourse.CreateEntity();
        studentCourse.StudentCourseId = this.studentCourse.StudentCourseId;
        studentCourse.HideInReporting = !visibleInReporting;

        this.saving = true;

        this.dataService.saveEntity(studentCourse)
            .pipe(finalize(() => this.saving = false))
            .subscribe(savedStudentCourse => {
                this.visibleInReporting = !savedStudentCourse.HideInReporting;
                this.formApi.patchFormData({ HideInReporting: savedStudentCourse.HideInReporting });

                this.dwEventService.publishEvent(
                    DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.StudentCourse),
                    savedStudentCourse
                );
            });
    }
}
